import type { CommonConsumer, IShopper, MaritalStatus } from '@makemydeal/dr-platform-types';
import type { BirthDate } from '@makemydeal/dr-common-utils';

export type combineCommonConsumerContactInfoOptions = {
    includeName?: boolean;
};

export const getFirstFiveDigitsPostalCode = (postalCode: string) => {
    return postalCode && postalCode.length > 5 ? postalCode.substring(0, 5) : postalCode;
};

export const transformBirthDate = (dateOfBirth: string): BirthDate => {
    const dateStr = dateOfBirth.trim();

    // Handle ISO date format (YYYY-MM-DD or YYYY-M-D) with or without time component
    // This will skip timezone processing and just extract the date part
    const isoDateOnlyMatch = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (isoDateOnlyMatch) {
        const [, year, month, day] = isoDateOnlyMatch;
        return {
            birthDay: day.padStart(2, '0'),
            birthMonth: month.padStart(2, '0'),
            birthYear: year
        };
    }

    // Handle US date format (M/D/YYYY or MM/DD/YYYY)
    const usMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (usMatch) {
        const [, month, day, year] = usMatch;
        return {
            birthDay: day.padStart(2, '0'),
            birthMonth: month.padStart(2, '0'),
            birthYear: year
        };
    }

    // Fallback: parse with Date constructor
    // Only add UTC time if no timezone info is present AND it looks like a date-only string
    const hasTimezone = dateStr.includes('T') || dateStr.includes('+') || dateStr.includes('Z');
    const looksLikeDateOnly = /^\d{4}-\d{1,2}-\d{1,2}$/.test(dateStr);
    const parseDateStr = hasTimezone || !looksLikeDateOnly ? dateOfBirth : dateOfBirth + 'T00:00:00.000Z';
    const date = new Date(parseDateStr);

    return {
        birthDay: String(date.getDate()).padStart(2, '0'),
        birthMonth: String(date.getMonth() + 1).padStart(2, '0'),
        birthYear: String(date.getFullYear())
    };
};

/**
 * Convert CommonConsumer to IShopper, but only consume the address and contact fields.
 * @param original
 * @param customer
 * @param allowEmptyValues
 * @returns
 */
export const combineCommonConsumerContactInfo = (
    original: IShopper,
    // `creditPullConsentGiven` is on IShopper but not yet on CommonConsumer in dr-platform-types.
    customer: CommonConsumer & { creditPullConsentGiven?: boolean },
    allowEmptyValues: boolean,
    optionalDesking: boolean = false,
    isPostalCodeDigitsOnlyValidationEnabled: boolean = false
): IShopper => {
    let updatedShopperInfo: IShopper = {
        ...original
    };

    // if any field is present; drop the entire old address and replace it with the new.
    if (
        customer.city ||
        customer.stateOrProvinceAbbreviation ||
        customer.postalCode ||
        customer.street1 ||
        customer.street2 ||
        customer.street3 ||
        customer.countyOrRegion
    ) {
        delete updatedShopperInfo.streetAddress;
        delete updatedShopperInfo.streetAddress2;
        delete updatedShopperInfo.streetAddress3;
        delete updatedShopperInfo.city;
        delete updatedShopperInfo.state;
        updatedShopperInfo.zip = ''; // zip is a required field, so set to '' instead of deleting
        delete updatedShopperInfo.county;
    }

    const customerPhone = [customer.cellPhone, customer.homePhone, customer.workPhone].find((p) => p ?? false);
    const customerBirthDate = customer.dateOfBirth && transformBirthDate(customer.dateOfBirth);

    const shouldIncludeField = (value: string | undefined): boolean => !!value || allowEmptyValues;
    const shouldIncludeRequiredField = (value: string | undefined): boolean => !!value || optionalDesking;

    updatedShopperInfo = {
        ...updatedShopperInfo,
        commonConsumerId: customer.consumerId,

        // required values
        ...(shouldIncludeRequiredField(customer.firstName) && { firstName: customer.firstName }),
        ...(shouldIncludeRequiredField(customer.lastName) && { lastName: customer.lastName }),
        ...(shouldIncludeRequiredField(customer.primaryEmail) && { email: customer.primaryEmail?.toLowerCase() }),
        ...(shouldIncludeRequiredField(customer.postalCode) && {
            zip: isPostalCodeDigitsOnlyValidationEnabled
                ? customer.postalCode ?? ''
                : getFirstFiveDigitsPostalCode(customer.postalCode ?? '')
        }),
        ...(customer.business && { business: customer.business }),

        // Optional fields: only include if value exists OR allowEmptyValues is true
        // Address fields
        ...(shouldIncludeField(customer.street1) && { streetAddress: customer.street1 }),
        ...(shouldIncludeField(customer.street2) && { streetAddress2: customer.street2 }),
        ...(shouldIncludeField(customer.street3) && { streetAddress3: customer.street3 }),
        ...(shouldIncludeField(customer.city) && { city: customer.city }),
        ...(shouldIncludeField(customer.stateOrProvinceAbbreviation) && { state: customer.stateOrProvinceAbbreviation }),
        ...(shouldIncludeField(customer.countyOrRegion) && { county: customer.countyOrRegion }),

        // Contact fields
        ...(shouldIncludeField(customer.alternateEmail) && { emailAlternative: customer.alternateEmail?.toLowerCase() }),
        ...(shouldIncludeField(customerPhone) && { phone: customerPhone }),
        ...(shouldIncludeField(customer.cellPhone) && { cellPhone: customer.cellPhone }),
        ...(shouldIncludeField(customer.homePhone) && { homePhone: customer.homePhone }),
        ...(shouldIncludeField(customer.workPhone) && { workPhone: customer.workPhone }),

        // Personal information fields
        ...(shouldIncludeField(customer.middleName) && { middleName: customer.middleName }),
        ...(shouldIncludeField(customer.suffix) && { suffix: customer.suffix }),
        ...(shouldIncludeField(customer.title) && { title: customer.title }),
        ...(shouldIncludeField(customer.legalName) && { legalName: customer.legalName }),
        ...(customer.customerType && { customerType: customer.customerType }),
        ...(customerBirthDate && { birthDate: customerBirthDate }),

        // Personal information additional fields
        ...(customer.maritalStatus && { maritalStatus: customer.maritalStatus as MaritalStatus }),
        ...(customer.relationshipToBuyer && { relationshipToBuyer: customer.relationshipToBuyer }),
        ...(customer.participantRole && { participantRole: customer.participantRole }),

        // Housing & employment additional fields
        ...(customer.housingInfo && { housingInfo: customer.housingInfo }),
        ...(customer.previousAddress && { previousAddress: customer.previousAddress }),
        ...(customer.currentEmployment && { currentEmployment: customer.currentEmployment }),
        ...(customer.previousEmployment && { previousEmployment: customer.previousEmployment }),
        ...(customer.financialInfo && { financialInfo: customer.financialInfo })
    };

    const shopperKey: keyof IShopper = 'billingAddress';
    if (shopperKey in customer) {
        updatedShopperInfo[shopperKey] = customer[shopperKey];
    }

    // Use an `in` check (like billingAddress) so an explicitly cleared value (undefined) overwrites
    // the original, while flows that omit these keys leave the existing values untouched.
    if ('yearsAtCurrentAddress' in customer) {
        updatedShopperInfo.yearsAtCurrentAddress = customer.yearsAtCurrentAddress;
    }
    if ('monthsAtCurrentAddress' in customer) {
        updatedShopperInfo.monthsAtCurrentAddress = customer.monthsAtCurrentAddress;
    }

    if ('yearsAtPreviousAddress' in customer) {
        updatedShopperInfo.yearsAtPreviousAddress = customer.yearsAtPreviousAddress;
    }
    if ('monthsAtPreviousAddress' in customer) {
        updatedShopperInfo.monthsAtPreviousAddress = customer.monthsAtPreviousAddress;
    }
    if ('creditPullConsentGiven' in customer) {
        updatedShopperInfo.creditPullConsentGiven = customer.creditPullConsentGiven;
    }

    return updatedShopperInfo;
};
